/**
 * IntelReportPage — Minimal public page for viewing/downloading a match intelligence report.
 * Used by the landing page "Download Report" button: /intel-report?matchNo=N
 *
 * Does NOT require login for completed matches.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../services/api';

// ── Theme (matches IntelligencePage colours) ──────────────────────────────────
const T = {
  bg: '#0C0C0E', surface: '#141416', surface2: '#1C1C20',
  border: 'rgba(255,255,255,0.08)', text: '#F0EDE8',
  muted: '#7A7A85', green: '#2ECC71', yellow: '#F1C40F', red: '#E74C3C',
};

const FONT_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@400;500&display=swap');
  .irl-bebas { font-family: 'Bebas Neue', sans-serif !important; }
  .irl-mono  { font-family: 'DM Mono', monospace !important; }
  @keyframes irl-spin  { to { transform: rotate(360deg); } }
  @keyframes irl-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
`;

// ── Tiny spinner ──────────────────────────────────────────────────────────────
function Spinner({ message }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 16, padding: 60,
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        border: `4px solid ${T.border}`,
        borderTopColor: T.yellow,
        animation: 'irl-spin 0.8s linear infinite',
      }} />
      <div style={{ color: T.text, fontSize: 14, fontFamily: "'DM Mono',monospace", letterSpacing: '0.06em' }}>
        {message || 'Loading…'}
      </div>
    </div>
  );
}

// ── Download PDF helper (same logic as IntelligencePage) ─────────────────────
async function downloadPdf(reportEl, team1Short, team2Short, matchDate) {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);

  reportEl.setAttribute('data-pdf-export-root', 'true');

  const canvas = await html2canvas(reportEl, {
    scale: window.innerWidth <= 768 ? 1.35 : 1.6,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    windowWidth: reportEl.scrollWidth,
    windowHeight: reportEl.scrollHeight,
    onclone: (doc) => {
      const style = doc.createElement('style');
      style.textContent = `
        [data-pdf-export-root='true'] { background:#ffffff!important; color:#111!important; }
        [data-pdf-export-root='true'] * { animation:none!important; }
        [data-pdf-export-root='true'] button,[data-pdf-export-root='true'] [data-pdf-hide='true'] { display:none!important; }
        [data-pdf-export-root='true'] [style*='background:#141416'],
        [data-pdf-export-root='true'] [style*='background:#1C1C20'],
        [data-pdf-export-root='true'] [style*='background:#0C0C0E'] { background:#f7fbff!important; }
        [data-pdf-export-root='true'] [style*='color:'] { color:#111!important; }
      `;
      doc.head.appendChild(style);
    },
  });

  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const margin = 6;
  const pageW = pw - margin * 2;
  const pageH = ph - margin * 2;
  const pxPerMm = canvas.width / pageW;
  const pageHpx = Math.floor(pageH * pxPerMm);

  let offsetY = 0, page = 0;
  while (offsetY < canvas.height) {
    const sliceH = Math.min(pageHpx, canvas.height - offsetY);
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = sliceH;
    const ctx = slice.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, offsetY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
    const img = slice.toDataURL('image/jpeg', 0.72);
    if (page > 0) pdf.addPage();
    pdf.addImage(img, 'JPEG', margin, margin, pageW, sliceH / pxPerMm, undefined, 'FAST');
    offsetY += sliceH;
    page++;
  }

  pdf.save(`IPLTrade-Intel-${team1Short}-vs-${team2Short}-${matchDate || 'report'}.pdf`);
  reportEl.removeAttribute('data-pdf-export-root');
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function IntelReportPage() {
  const [phase, setPhase]       = useState('init');   // init|loading|done|error
  const [message, setMessage]   = useState('');
  const [report, setReport]     = useState(null);
  const [postMatch, setPostMatch] = useState(null);
  const [matchInfo, setMatchInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);

  // Read matchNo from URL query string
  const matchNo = new URLSearchParams(window.location.search).get('matchNo');

  const loadReport = useCallback(async () => {
    if (!matchNo) {
      setPhase('error');
      setMessage('No match number specified in URL (expected ?matchNo=N).');
      return;
    }

    setPhase('loading');
    setMessage('Loading match schedule…');

    try {
      // 1. Fetch schedule to find the match
      const schedRes = await API.get('/intelligence/schedule');
      const matches = schedRes.data.matches || [];
      const match = matches.find(m => String(m.matchNo) === String(matchNo));

      if (!match) {
        setPhase('error');
        setMessage(`Match #${matchNo} not found in the schedule.`);
        return;
      }

      if (match.status !== 'completed') {
        setPhase('error');
        setMessage('This match has not yet been completed. Please log in to view upcoming match analytics.');
        return;
      }

      setMatchInfo(match);
      setMessage('Generating intelligence report…');

      // 2. Fetch report + postmatch in parallel
      const params = new URLSearchParams({ team1: match.team1, team2: match.team2 });
      if (match.venue)   params.set('venue',   match.venue);
      if (match.matchNo) params.set('matchNo', match.matchNo);
      if (match.date)    params.set('date',    match.date);
      if (match.status)  params.set('status',  match.status);  // critical for guest access

      const [predRes, postRes] = await Promise.all([
        API.get('/intelligence/predict?' + params.toString()),
        API.get('/intelligence/postmatch?team1=' + match.team1 + '&team2=' + match.team2 + (match.date ? '&date=' + match.date : '')),
      ]);

      if (predRes.data?.pending) {
        setPhase('error');
        setMessage(predRes.data.reason || 'Teams not yet decided for this fixture.');
        return;
      }

      setReport(predRes.data);
      setPostMatch(postRes.data);
      setPhase('done');
    } catch (err) {
      if (err.response?.status === 401) {
        setPhase('error');
        setMessage('Please log in to view this match report.');
        return;
      }
      setPhase('error');
      setMessage(err.response?.data?.error || err.message || 'Failed to load report. Please try again.');
    }
  }, [matchNo]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleDownload = async () => {
    if (!reportRef.current || !report) return;
    setDownloading(true);
    try {
      await downloadPdf(
        reportRef.current,
        report.team1?.short || 'T1',
        report.team2?.short || 'T2',
        report.matchDate,
      );
    } catch (err) {
      console.error('PDF download failed:', err);
      alert('PDF generation failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // ── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{FONT_STYLE}</style>
      <div style={{
        minHeight: '100dvh',
        background: T.bg,
        color: T.text,
        fontFamily: "'Inter',system-ui,sans-serif",
      }}>
        {/* Header bar */}
        <div style={{
          height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 20px', borderBottom: `1px solid ${T.border}`,
          background: 'rgba(10,14,26,0.97)', position: 'sticky', top: 0, zIndex: 100,
        }}>
          <a href="/" style={{ color: T.yellow, fontWeight: 700, fontSize: '1.1rem', textDecoration: 'none', fontFamily: "'DM Mono',monospace", letterSpacing: '0.06em' }}>
            IPLTRADE
          </a>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {phase === 'done' && (
              <button
                data-pdf-hide="true"
                onClick={handleDownload}
                disabled={downloading}
                style={{
                  padding: '8px 18px', borderRadius: 4,
                  background: T.yellow, color: '#111', border: 'none',
                  fontWeight: 700, fontSize: 12, cursor: downloading ? 'not-allowed' : 'pointer',
                  opacity: downloading ? 0.7 : 1,
                  fontFamily: "'DM Mono',monospace", letterSpacing: '0.08em', textTransform: 'uppercase',
                }}
              >
                {downloading ? 'Generating PDF…' : '⬇ Download PDF'}
              </button>
            )}
            <a href="/auth" style={{
              padding: '7px 14px', borderRadius: 4,
              background: 'transparent', color: T.muted,
              border: `1px solid ${T.border}`,
              fontSize: 12, textDecoration: 'none',
              fontFamily: "'DM Mono',monospace", letterSpacing: '0.06em',
            }}>
              Log In
            </a>
          </div>
        </div>

        {/* Content area */}
        <div style={{ padding: '0 0 40px' }}>
          {phase === 'init' && <Spinner message="Initialising…" />}
          {phase === 'loading' && <Spinner message={message} />}

          {phase === 'error' && (
            <div style={{
              maxWidth: 520, margin: '60px auto', padding: 32,
              background: T.surface, borderRadius: 8,
              border: `1px solid rgba(231,76,60,0.3)`,
              borderLeft: `4px solid ${T.red}`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 32, marginBottom: 16 }}>⚠️</div>
              <div style={{ color: T.text, fontSize: 15, marginBottom: 20, lineHeight: 1.7 }}>{message}</div>
              {(message.toLowerCase().includes('log in') || message.toLowerCase().includes('login')) && (
                <a href="/auth" style={{
                  display: 'inline-block', padding: '10px 28px', borderRadius: 6,
                  background: T.yellow, color: '#111', fontWeight: 700, fontSize: 13,
                  textDecoration: 'none', marginBottom: 12,
                }}>
                  → Log In to IPLTrade
                </a>
              )}
              <div>
                <a href="/" style={{ color: T.muted, fontSize: 12, textDecoration: 'none' }}>← Back to Home</a>
              </div>
            </div>
          )}

          {phase === 'done' && report && (
            <div>
              {/* The actual report content — exactly what's in IntelligencePage.jsx */}
              <div ref={reportRef} style={{ maxWidth: 1100, margin: '0 auto', padding: '0 16px' }}>
                <IntelReportContent report={report} postMatch={postMatch} matchInfo={matchInfo} />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Minimal report renderer (stripped-down PredictionReport) ─────────────────
function IntelReportContent({ report, postMatch, matchInfo }) {
  const t1 = report.team1;
  const t2 = report.team2;
  const vp = report.venueProfile;
  const h2h = report.h2h;
  const dp = report.winProbability || { t1: 50, t2: 50 };

  // Toss from postmatch
  const toss = postMatch?.toss || {};
  const hasToss = !!(toss.winner && toss.choice);
  const battingFirst = hasToss
    ? (toss.choice === 'bat' ? toss.winner : (toss.winner === 'team1' ? 'team2' : 'team1'))
    : null;
  const battingTeam = battingFirst === 'team1' ? t1 : battingFirst === 'team2' ? t2 : null;
  const bowlingTeam = battingFirst === 'team1' ? t2 : battingFirst === 'team2' ? t1 : null;

  return (
    <div style={{ color: T.text, fontFamily: "'DM Sans',sans-serif", paddingTop: 24 }}>

      {/* Report header */}
      <div style={{
        background: T.surface, borderRadius: 6,
        border: `1px solid ${T.border}`,
        padding: '18px 22px', marginBottom: 20,
      }}>
        <div className="irl-mono" style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 10 }}>
          Match {report.matchNo} · Pre-Match Intelligence Report
          {postMatch?.available && (
            <span style={{ color: T.green, marginLeft: 14, padding: '2px 8px', background: `${T.green}14`, border: `1px solid ${T.green}44`, borderRadius: 2 }}>
              ✓ Post-Match Data Available
            </span>
          )}
        </div>
        <div className="irl-bebas" style={{ fontSize: 'clamp(32px,5vw,58px)', lineHeight: 1, marginBottom: 10 }}>
          <span style={{ color: t1.color }}>{t1.short}</span>
          <span style={{ color: T.muted, fontSize: '0.5em', margin: '0 12px' }}>VS</span>
          <span style={{ color: t2.color }}>{t2.short}</span>
        </div>
        <div style={{ fontSize: 13, color: T.muted }}>
          {vp ? `Avg Score: ${vp.avgScore} · ${vp.pitchType} · Dew: ${vp.dewFactor}` : ''}
          {report.venue && <span style={{ marginLeft: 14 }}>📍 {report.venue}</span>}
          {report.matchDate && <span style={{ marginLeft: 14 }}>📅 {new Date(report.matchDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>}
        </div>
      </div>

      {/* Match result (if completed) */}
      {postMatch?.available && postMatch?.winner && (
        <div style={{
          background: T.surface, border: `1px solid ${T.border}`,
          borderLeft: `4px solid ${T.yellow}`, borderRadius: 6,
          padding: '14px 20px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        }}>
          <span className="irl-bebas" style={{ fontSize: 16, color: T.yellow, letterSpacing: '0.1em' }}>🏆 MATCH RESULT</span>
          <span style={{ color: T.text, fontWeight: 700 }}>{postMatch.winner}</span>
          {postMatch.winBy && <span style={{ color: T.muted }}>won by {postMatch.winBy}</span>}
        </div>
      )}

      {/* Innings scores */}
      {postMatch?.innings?.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {postMatch.innings.map((inn, i) => {
            const isT1 = inn.teamShort === t1.short || inn.teamName?.toLowerCase().includes(t1.short?.toLowerCase());
            const c = isT1 ? t1.color : t2.color;
            return (
              <div key={i} style={{
                background: T.surface, border: `1px solid ${T.border}`,
                borderLeft: `4px solid ${c}`, borderRadius: 6, padding: '12px 16px', flex: '1 1 200px',
              }}>
                <div className="irl-mono" style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                  Innings {i + 1} · {inn.teamName || (isT1 ? t1.short : t2.short)}
                </div>
                <div className="irl-bebas" style={{ fontSize: 30, color: c, lineHeight: 1 }}>{inn.scoreStr}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Win probability */}
      <div style={{ marginBottom: 20 }}>
        <div className="irl-mono" style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
          Win Probability (Pre-Match Model)
        </div>
        <div style={{ display: 'flex', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, overflow: 'hidden' }}>
          {[{ team: t1, pct: dp.t1 }, { team: t2, pct: dp.t2 }].map(({ team, pct }, i) => (
            <div key={i} style={{
              flex: 1, padding: '18px 22px',
              background: `${team.color}12`,
              textAlign: i === 1 ? 'right' : 'left',
              borderRight: i === 0 ? `1px solid ${T.border}` : 'none',
            }}>
              <div className="irl-bebas" style={{ fontSize: 18, color: team.color, letterSpacing: '0.05em' }}>{team.name}</div>
              <div className="irl-bebas" style={{ fontSize: 48, color: team.color, lineHeight: 1 }}>{pct}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Toss if available */}
      {hasToss && (
        <div style={{
          background: T.surface, border: `1px solid ${T.border}`,
          borderLeft: `4px solid ${T.yellow}`, borderRadius: 6,
          padding: '14px 20px', marginBottom: 20,
        }}>
          <span className="irl-bebas" style={{ fontSize: 15, color: T.yellow, letterSpacing: '0.1em', marginRight: 12 }}>🪙 TOSS</span>
          {battingTeam && bowlingTeam && (
            <span style={{ fontSize: 13 }}>
              <span style={{ color: battingTeam.color, fontWeight: 700 }}>{battingTeam.name}</span>
              <span style={{ color: T.muted }}> bat first · </span>
              <span style={{ color: bowlingTeam.color, fontWeight: 700 }}>{bowlingTeam.name}</span>
              <span style={{ color: T.muted }}> bowl first</span>
            </span>
          )}
        </div>
      )}

      {/* Projected scores */}
      {report.projectedScores && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[{ team: t1, score: report.projectedScores.team1 }, { team: t2, score: report.projectedScores.team2 }].map(({ team, score }, i) => (
            <div key={i} style={{
              flex: '1 1 200px', background: T.surface, border: `1px solid ${T.border}`,
              borderLeft: `4px solid ${team.color}`, borderRadius: 6, padding: '14px 18px',
            }}>
              <div className="irl-mono" style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                {team.name} · Batting Projection
              </div>
              <div className="irl-bebas" style={{ fontSize: 28, color: team.color }}>{score ? `${score.min} – ${score.max} runs` : '—'}</div>
            </div>
          ))}
        </div>
      )}

      {/* H2H */}
      {h2h && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: '16px 20px', marginBottom: 20 }}>
          <div className="irl-mono" style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Head-to-Head</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: h2h.last5?.length ? 12 : 0 }}>
            {[
              { label: 'Total Meetings', value: h2h.total, color: T.text },
              { label: `${t1.short} Wins`, value: h2h.t1, color: t1.color },
              { label: `${t2.short} Wins`, value: h2h.t2, color: t2.color },
              { label: 'Highest Total', value: h2h.high, color: T.yellow },
            ].map((item, i) => (
              <div key={i} style={{
                flex: '1 1 100px', background: T.surface2, border: `1px solid ${T.border}`,
                borderRadius: 4, padding: '10px 14px', textAlign: 'center',
              }}>
                <div className="irl-bebas" style={{ fontSize: 22, color: item.color, lineHeight: 1, marginBottom: 3 }}>{item.value}</div>
                <div className="irl-mono" style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{item.label}</div>
              </div>
            ))}
          </div>
          {h2h.last5?.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ color: T.muted, fontSize: 12 }}>Last 5:</span>
              {h2h.last5.map((w, i) => (
                <React.Fragment key={i}>
                  <span style={{ color: w === t1.short ? t1.color : t2.color, fontWeight: 700, fontSize: 12 }}>{w}</span>
                  {i < h2h.last5.length - 1 && <span style={{ color: T.border, fontSize: 12 }}>→</span>}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Key batters */}
      <PlayerSection
        title="Key Batters"
        players={[
          ...(report.team1Batters || []).slice(0, 3).map(p => ({ ...p, teamColor: t1.color, teamShort: t1.short })),
          ...(report.team2Batters || []).slice(0, 3).map(p => ({ ...p, teamColor: t2.color, teamShort: t2.short })),
        ]}
        postMatch={postMatch}
        type="batting"
      />

      {/* Key bowlers */}
      <PlayerSection
        title="Key Bowlers"
        players={[
          ...(report.team1Bowlers || []).slice(0, 2).map(p => ({ ...p, teamColor: t1.color, teamShort: t1.short })),
          ...(report.team2Bowlers || []).slice(0, 2).map(p => ({ ...p, teamColor: t2.color, teamShort: t2.short })),
        ]}
        postMatch={postMatch}
        type="bowling"
      />

      {/* Venue analysis */}
      {vp && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderTop: `3px solid #3b82f6`, borderRadius: 6, padding: '16px 20px', marginBottom: 20 }}>
          <div className="irl-mono" style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Venue & Pitch Analysis</div>
          {[
            { label: 'Pitch Type', value: vp.pitchType },
            { label: 'Average 1st Innings', value: `${vp.avgScore} runs` },
            { label: 'Dew Factor', value: vp.dewFactor },
            { label: 'Boundary Size', value: vp.boundarySize },
            { label: 'Spin Effectiveness', value: vp.spinEffectiveness },
            { label: 'Pace Effectiveness', value: vp.paceEffectiveness },
            { label: 'Chase Advantage', value: vp.chaseAdvantage ? 'Yes' : 'No / Neutral' },
          ].map(({ label, value }, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', padding: '7px 0',
              borderBottom: i < 6 ? `1px solid ${T.border}` : 'none', fontSize: 13,
            }}>
              <span style={{ color: T.muted }}>{label}</span>
              <span style={{ color: T.text, fontWeight: 600 }}>{value || '—'}</span>
            </div>
          ))}
          {vp.notes && (
            <div style={{ marginTop: 10, fontSize: 11, color: T.muted, fontStyle: 'italic', lineHeight: 1.7, padding: '8px 10px', background: T.surface2, borderRadius: 4 }}>
              {vp.notes}
            </div>
          )}
        </div>
      )}

      {/* Final prediction */}
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderLeft: `4px solid ${T.yellow}`, borderRadius: 6, padding: '16px 20px', marginBottom: 30,
      }}>
        <div className="irl-bebas" style={{ fontSize: 14, color: T.yellow, letterSpacing: '0.1em', marginBottom: 8 }}>⚡ FINAL PREDICTION</div>
        <div style={{ fontSize: 14, lineHeight: 1.8 }}>
          <strong style={{ color: t1.color }}>{t1.name}</strong>{' '}
          {dp.t1 >= dp.t2 ? `hold a ${dp.t1}–${dp.t2} advantage.` : `are underdogs at ${dp.t1}–${dp.t2}.`}
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, color: T.muted }}>
          <div>🏆 Predicted: <strong style={{ color: dp.t1 >= dp.t2 ? t1.color : t2.color }}>{dp.t1 >= dp.t2 ? t1.name : t2.name}</strong></div>
          {postMatch?.winner && <div>✅ Actual: <strong style={{ color: T.green }}>{postMatch.winner}</strong>{postMatch.winBy ? ` · ${postMatch.winBy}` : ''}</div>}
        </div>
      </div>

      {/* Footer / disclaimer */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: '16px 0', marginTop: 8 }}>
        <div style={{
          background: T.surface, border: `1px solid ${T.border}`,
          borderLeft: `3px solid ${T.red}`, borderRadius: 4,
          padding: '12px 16px', marginBottom: 12,
        }}>
          <div className="irl-mono" style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Legal Disclaimer</div>
          <div style={{ fontSize: 10, color: T.muted, lineHeight: 1.7 }}>
            This report is for informational and entertainment purposes only. Not financial, betting, or investment advice.
            Data may contain delays or inaccuracies. IPLTrade does not guarantee outcome accuracy.
            All team/player names remain property of their respective owners.
          </div>
        </div>
        <div style={{ fontSize: 10, color: T.muted, textAlign: 'center' }}>
          © 2026 IPLTrade.in · support@ipltrade.in · Not affiliated with BCCI or the Indian Premier League.
        </div>
      </div>
    </div>
  );
}

// ── Player Section (batters or bowlers) ───────────────────────────────────────
function PlayerSection({ title, players, postMatch, type }) {
  if (!players?.length) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <div className="irl-mono" style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
        {title}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {players.map((p, i) => {
          const isBatter = type === 'batting';
          const actual = postMatch?.available
            ? isBatter
              ? (postMatch.allBatters || []).find(b => normalize(b.name) === normalize(p.name))
              : (postMatch.allBowlers || []).find(b => normalize(b.name) === normalize(p.name))
            : null;

          return (
            <div key={i} style={{
              background: T.surface, borderRadius: 6, overflow: 'hidden',
              border: `1px solid ${T.border}`, borderLeft: `3px solid ${p.teamColor}`,
            }}>
              <div style={{ padding: '11px 14px', borderBottom: `1px solid ${T.border}` }}>
                <div className="irl-bebas" style={{ fontSize: 18, color: T.text, letterSpacing: '0.04em', lineHeight: 1 }}>{p.name}</div>
                <div className="irl-mono" style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 }}>
                  {p.teamShort} · {p.role}
                </div>
                <div style={{ marginTop: 5 }}>
                  <span style={{
                    fontSize: 9, padding: '2px 8px', borderRadius: 2,
                    background: `rgba(${p.impact === 'HIGH' ? '39,174,96' : p.impact === 'MEDIUM' ? '241,196,15' : '231,76,60'},0.18)`,
                    color: p.impact === 'HIGH' ? T.green : p.impact === 'MEDIUM' ? T.yellow : T.red,
                    border: `1px solid rgba(${p.impact === 'HIGH' ? '39,174,96' : p.impact === 'MEDIUM' ? '241,196,15' : '231,76,60'},0.3)`,
                    fontFamily: "'DM Mono',monospace", letterSpacing: '0.1em', textTransform: 'uppercase',
                  }}>
                    ● {p.impact}
                  </span>
                </div>
              </div>
              <div style={{ padding: '10px 14px', fontSize: 12 }}>
                <div style={{ color: T.muted, marginBottom: 4 }}>
                  Predicted: <span style={{ color: T.text, fontWeight: 600 }}>{p.prediction?.range || '—'}</span>
                </div>
                {actual && (
                  <div style={{ color: T.muted }}>
                    Actual: <span style={{ color: T.green, fontWeight: 700 }}>
                      {isBatter ? `${actual.runs} runs (${actual.balls}b)` : `${actual.wickets}W / ${actual.runs}R (${actual.overs}ov)`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function normalize(name) {
  return String(name || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}
